import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import { Dialog, DialogContent, DialogTrigger } from "../../../../components/ui/dialog"
import { useWidget } from "../../../../providers/widget"
import { useExplorer } from "../../providers"
import { useExplorerCss } from "../../hooks/useExplorerCss"
import { curateOptions, priceFieldForMode, useFetchExplorerMeta } from "../../data"
import { ExploreWidgetConfig } from "../../config"
import NumberControl from "./NumberControl"
import PriceRangeSlider from "./PriceRangeSlider"
import WhatPopover from "./pro/WhatPopover"

/**
 * Filters field. The two variants no longer share a surface (#870 slice 5):
 * `pro` opens the toolkit's tabbed WHAT popover with live-apply semantics,
 * while legacy keeps the Apply/Clear modal below. They are separate components
 * so each owns its own hooks; the router preserves the `<AdditionalFilters pro />`
 * call sites in ProFilterBar and the legacy FilterBar. `popover` (desktop pro
 * bar instances only) is forwarded so WhatPopover anchors under the bar as a
 * scrimless popover like When/Who.
 */
export default function AdditionalFilters({ pro = false, popover = false, sheet = false, sheetActive = false, onSheetActivate, advanceSignal = 0 }: { pro?: boolean; popover?: boolean; sheet?: boolean; sheetActive?: boolean; onSheetActivate?: () => void; advanceSignal?: number } = {}) {
  return pro ? <WhatPopover popover={popover} sheet={sheet} sheetActive={sheetActive} onSheetActivate={onSheetActivate} advanceSignal={advanceSignal} /> : <LegacyAdditionalFilters />
}

function LegacyAdditionalFilters() {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const cssString = useExplorerCss()
  const [open, setOpen] = useState(false)

  // Slider bounds come from the price column matching the browse (no-dates) price mode (#441).
  const priceField = priceFieldForMode(options.priceDisplay.noDates)
  const { data: metaData } = useFetchExplorerMeta(feedId, options.filter?.platform, priceField)
  const meta = metaData?.result

  const priceMin = meta?.priceRange?.min ?? 0
  const priceMax = meta?.priceRange?.max ?? 10000

  // Curate the Types / Amenities option lists to the admin's selection + order (#511).
  const curatedTypes = useMemo(
    () => curateOptions(meta?.taxonomies?.types ?? [], options.filterOptions?.types?.split(',').filter(Boolean)),
    [meta?.taxonomies?.types, options.filterOptions]
  )
  // Amenities differ from the other dimensions (#556, WP parity): only the
  // explicitly selected amenities render, and an empty selection renders
  // NONE — the WP plugin skips the term query entirely when
  // explorer_terms_property_amenity is empty, so the section disappears.
  const curatedAmenities = useMemo(() => {
    const selected = options.filterOptions?.amenities?.split(',').filter(Boolean) ?? []
    if (selected.length === 0) return []
    return curateOptions(meta?.taxonomies?.amenities ?? [], selected)
  }, [meta?.taxonomies?.amenities, options.filterOptions])

  // Local state for the modal
  const [keywords, setKeywords] = useState(state.visitorFilters.keywords ?? '')
  const [priceRange, setPriceRange] = useState<[number, number]>([
    state.visitorFilters.price_min ?? priceMin,
    state.visitorFilters.price_max ?? priceMax,
  ])
  // meta loads async, so the useState above captures the 0/10000 fallback on the
  // first render. Once the real priceRange arrives, snap the slider to it — unless
  // the visitor already set a price filter or dragged the slider this session.
  const priceTouched = useRef(false)
  useEffect(() => {
    if (priceTouched.current) return
    if (state.visitorFilters.price_min == null && state.visitorFilters.price_max == null) {
      setPriceRange([priceMin, priceMax])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMin, priceMax])
  const [bedrooms, setBedrooms] = useState(state.visitorFilters.bedrooms ?? 0)
  const [bathrooms, setBathrooms] = useState(state.visitorFilters.bathrooms ?? 0)
  const [featured, setFeatured] = useState(state.visitorFilters.featured ?? false)
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    state.visitorFilters.types?.split(',').filter(Boolean) ?? []
  )
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>(
    (state.visitorFilters.amenities ?? state.visitorFilters.amenities_and)?.split(',').filter(Boolean) ?? []
  )
  const [showAllAmenities, setShowAllAmenities] = useState(false)

  // Re-seed the draft from the APPLIED filters on every open: quick chips can
  // mutate types/amenities behind this dialog's back, and a mount-time draft
  // would otherwise apply stale values over them.
  useEffect(() => {
    if (!open) return
    const vf = state.visitorFilters
    setKeywords(vf.keywords ?? '')
    setPriceRange([vf.price_min ?? priceMin, vf.price_max ?? priceMax])
    priceTouched.current = vf.price_min != null || vf.price_max != null
    setBedrooms(vf.bedrooms ?? 0)
    setBathrooms(vf.bathrooms ?? 0)
    setFeatured(vf.featured ?? false)
    setSelectedTypes(vf.types?.split(',').filter(Boolean) ?? [])
    setSelectedAmenities((vf.amenities ?? vf.amenities_and)?.split(',').filter(Boolean) ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Toolkit port (#852): bedrooms/bathrooms can be hidden independently
  // (Searchbar Pro filter_show_bedrooms/filter_show_bathrooms). A hidden input
  // must be inert everywhere — render, modification detection, AND apply — or a
  // retained value keeps filtering invisibly. Absent = shown (current behavior).
  const showBedrooms = options.showBedroomsFilter !== false
  const showBathrooms = options.showBathroomsFilter !== false

  const hasModifications = keywords || priceRange[0] > priceMin || priceRange[1] < priceMax || (showBedrooms && bedrooms > 0) || (showBathrooms && bathrooms > 0) || featured || selectedTypes.length > 0 || selectedAmenities.length > 0

  const handleApply = () => {
    // Send amenities under amenities_and (must have all) or amenities (any) per config.
    const amenitiesCsv = selectedAmenities.length > 0 ? selectedAmenities.join(',') : undefined
    const matchAllAmenities = options.requireAllAmenities === true
    setFilter({
      keywords: keywords || undefined,
      types: selectedTypes.length > 0 ? selectedTypes.join(',') : undefined,
      amenities: matchAllAmenities ? undefined : amenitiesCsv,
      amenities_and: matchAllAmenities ? amenitiesCsv : undefined,
      featured: featured || undefined,
      bedrooms: showBedrooms && bedrooms > 0 ? bedrooms : undefined,
      bathrooms: showBathrooms && bathrooms > 0 ? bathrooms : undefined,
      price_min: priceRange[0] > priceMin ? priceRange[0] : undefined,
      price_max: priceRange[1] < priceMax ? priceRange[1] : undefined,
    })
    setOpen(false)
  }

  const handleClear = () => {
    setKeywords('')
    priceTouched.current = false
    setPriceRange([priceMin, priceMax])
    setBedrooms(0)
    setBathrooms(0)
    setFeatured(false)
    setSelectedTypes([])
    setSelectedAmenities([])
  }

  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    )
  }

  const toggleAmenity = (amenity: string) => {
    setSelectedAmenities(prev =>
      prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]
    )
  }

  // Which popup sections the admin chose to show (#554, WP: additional_filter_fields).
  // Fall back to all sections when unset so older configs keep their current behavior.
  const enabledFilters = options.additionalFilters ?? ['keyword', 'price', 'rooms', 'types', 'amenities']
  const showKeyword = enabledFilters.includes('keyword')
  const showPrice = enabledFilters.includes('price')
  // The rooms section collapses entirely when both steppers are disabled (#852).
  const showRooms = enabledFilters.includes('rooms') && (showBedrooms || showBathrooms)
  const showTypes = enabledFilters.includes('types')
  const showAmenities = enabledFilters.includes('amenities')
  const section1Visible = showKeyword || showPrice
  const section2Visible = showRooms || showTypes
  const amenitiesVisible = showAmenities && curatedAmenities.length > 0

  // Show the first N amenities with a "More Amenities" expander for the rest,
  // matching the WP plugin (#555, WP: primary_features_number). Expand-once.
  const primaryAmenitiesCount = options.primaryAmenitiesCount ?? 8
  const amenitiesColumns = options.amenitiesColumns ?? 3
  const hasMoreAmenities = curatedAmenities.length > primaryAmenitiesCount
  const visibleAmenities = showAllAmenities || !hasMoreAmenities
    ? curatedAmenities
    : curatedAmenities.slice(0, primaryAmenitiesCount)

  const renderTrigger = () => (
    <button type="button" className={`hk-button hfse-form-additional-filters-btn ${hasModifications ? 'modified' : ''}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
      </svg>
      <span className="hfse-form-additional-filters-btn-text">Filters</span>
      <span className="hfse-form-additional-filters-state"></span>
    </button>
  )

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {renderTrigger()}
        </DialogTrigger>
        <DialogContent className="hk-text-default hfse-form-modal hfse-form-additional-filters-options" elevateDom cssString={cssString} title="Filters" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="hk-modal-header">
            <div className="hk-modal-header-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span className="hk-h4">Filters</span>
            </div>
          </div>

          <div className="hk-modal-body" style={{ overflowY: 'auto' }}>
            {/* Section 1: Search by Name + Price Range */}
            {section1Visible && (
              <div className="hfse-af-grid hfse-af-grid-divider">
                {showKeyword && (
                  <div className={`hfse-af-col ${showPrice ? 'hfse-af-col-1-2' : 'hfse-af-col-full'}`}>
                    <div className="hk-h6 hfse-af-label">Search by Name</div>
                    <div className="hfse-af-search-input-wrap">
                      <span className="hfse-af-search-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        name="title__like"
                        className="hk-input hfse-af-search-input"
                        value={keywords}
                        onChange={e => setKeywords(e.target.value)}
                        placeholder="Search..."
                      />
                    </div>
                  </div>
                )}
                {showPrice && (
                  <div className={`hfse-af-col ${showKeyword ? 'hfse-af-col-1-2' : 'hfse-af-col-full'}`}>
                    <div className="hk-h6 hfse-af-label">
                      Price Range (${priceRange[0].toLocaleString()} - ${priceRange[1].toLocaleString()})
                    </div>
                    <PriceRangeSlider
                      min={priceMin}
                      max={priceMax}
                      step={options.priceStep ?? 5}
                      valueMin={priceRange[0]}
                      valueMax={priceRange[1]}
                      onChange={(lo, hi) => {
                        priceTouched.current = true
                        setPriceRange([lo, hi])
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {section1Visible && section2Visible && <hr className="hfse-af-divider" />}

            {/* Section 2: Bedrooms/Bathrooms + Types of Place */}
            {section2Visible && (
              <div className="hfse-af-grid hfse-af-grid-divider">
                {showRooms && (
                  <div className={`hfse-af-col ${showTypes ? 'hfse-af-col-1-3' : 'hfse-af-col-full'}`}>
                    {showBedrooms && (
                      <div className="hfse-af-room-group">
                        <div className="hk-h6 hfse-af-label">Bedrooms</div>
                        <NumberControl name="bedrooms" value={bedrooms} min={0} onChange={setBedrooms} label="Bedrooms" />
                      </div>
                    )}
                    {showBathrooms && (
                      <div className="hfse-af-room-group">
                        <div className="hk-h6 hfse-af-label">Bathrooms</div>
                        <NumberControl name="bathrooms" value={bathrooms} min={0} onChange={setBathrooms} label="Bathrooms" />
                      </div>
                    )}
                  </div>
                )}
                {showTypes && (
                  <div className={`hfse-af-col ${showRooms ? 'hfse-af-col-2-3' : 'hfse-af-col-full'}`}>
                    <div className="hk-h6 hfse-af-label">Types of Place</div>
                    <hr className="hfse-af-divider-thin" />
                    <div className="hfse-af-checkbox-grid">
                      {curatedTypes.map(type => (
                        <label key={type.id} className="hfse-af-checkbox-label">
                          <input
                            className="hk-checkbox"
                            type="checkbox"
                            checked={selectedTypes.includes(String(type.id))}
                            onChange={() => toggleType(String(type.id))}
                          />
                          <span>{type.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Section 3: Features & Amenities */}
            {amenitiesVisible && (
              <>
                {(section1Visible || section2Visible) && <hr className="hfse-af-divider" />}
                <div>
                  <div className="hk-h6 hfse-af-label">Features & Amenities</div>
                  <div
                    className="hfse-af-checkbox-grid"
                    style={{ '--hfse-af-columns': amenitiesColumns } as CSSProperties}
                  >
                    {visibleAmenities.map(amenity => (
                      <label key={amenity.id} className="hfse-af-checkbox-label">
                        <input
                          className="hk-checkbox"
                          type="checkbox"
                          checked={selectedAmenities.includes(String(amenity.id))}
                          onChange={() => toggleAmenity(String(amenity.id))}
                        />
                        <span>{amenity.name}</span>
                      </label>
                    ))}
                  </div>
                  {hasMoreAmenities && !showAllAmenities && (
                    <button
                      type="button"
                      className="hk-button hk-button-link hfse-af-more-amenities"
                      onClick={() => setShowAllAmenities(true)}
                    >
                      More Amenities
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="hk-modal-footer">
            {hasModifications && (
              <button type="button" className="hk-button hk-button-link hfse-modal-reset-button hfse-show-always" onClick={handleClear}>
                Clear Filters
              </button>
            )}
            <button
              type="button"
              className="hk-button hk-button-small bg-accent text-accent-foreground hfse-modal-next-button"
              onClick={handleApply}
              style={{ marginLeft: 'auto' }}
            >
              Apply
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
